import React, { useState, useEffect, useRef } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    TextField,
    Button,
    useMediaQuery,
    useTheme
} from '@mui/material';

const blank = {
    Make: '',
    Model: '',
    Year: '',
    License: '',
    VIN: '',
    AHP: '',
    DateAcquired: '',
    DashcamSerial: '',
    SMS: '',
    Comments: ''
};

// Dashcam and SMS are being replaced by Samsara, so new vehicles skip them;
// existing vehicles keep them editable until the migration is finished.
const FIELD_DEFS = [
    { name: 'Make', label: 'Make' },
    { name: 'Model', label: 'Model' },
    { name: 'Year', label: 'Year', inputProps: { inputMode: 'numeric', pattern: '[0-9]*' } },
    { name: 'License', label: 'License' },
    { name: 'VIN', label: 'VIN' },
    { name: 'AHP', label: 'AHP (Accessibility Handicap Permit)' },
    { name: 'DateAcquired', label: 'Date of Purchase', type: 'date', inputProps: { min: '1980-01-01' }, InputLabelProps: { shrink: true } },
    { name: 'DashcamSerial', label: 'Dashcam Serial Number', editOnly: true },
    { name: 'SMS', label: 'SMS (Satellite Monitoring System)', editOnly: true },
    { name: 'Comments', label: 'Comments', multiline: true, minRows: 3, xs: 12, sm: 12 }
];

/**
 * VehicleDialog renders a modal form for adding or editing a vehicle record.
 * It initializes state from the given "initial" vehicle, normalizes the AHP/OtherId field,
 * and calls "onSave" with the payload on submit.
 * 
 * @param {Object} props
 * @param {boolean} props.open - Whether the dialog is visible.
 * @param {Object | null} props.initial - Existing vehicle data to edit, or null to create a new vehicle.
 * @param {(vehicle: Object) => Promise<boolean>} props.onSave - Invoked with the prepared vehicle payload when the user clicks "Save"; resolves to whether it succeeded.
 * @param {() => void} props.onClose - Invoked when the dialog should be closed.
 *
 * @returns {JSX.Element}
 */
const VehicleDialog = ({ open, initial, onSave, onClose }) => {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const [v, setV] = useState(blank);
    const [dateInvalid, setDateInvalid] = useState(false);
    const [saving, setSaving] = useState(false);
    // Ref guard instead of disabling Save, so keyboard focus stays on the button
    const savingRef = useRef(false);
    const dateRef = useRef(null);
    const today = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Toronto' });

    // Keyed on "open" too: reopening Add (initial stays null) must clear the last attempt.
    useEffect(() => {
        if (!open) return;
        setV(initial ? { ...blank, ...initial, AHP: initial.AHP ?? initial.OtherId ?? '' } : blank);
        setDateInvalid(false);
    }, [open, initial]);

    /**
     * Handles text field changes and updates the local vehicle form state.
     *
     * @param {React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement} e
     * @returns
     */
    const set = (e) => {
        // Only clear here; raising on every keystroke flickers while the year is retyped (0002, 0020, ...)
        if (e.target.type === 'date' && e.target.validity.valid) setDateInvalid(false);
        setV(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    /**
     * Builds the vehicle payload (including mapping AHP to OtherId) and passes it to "OnSave".
     * Keeps the dialog open if the save fails so the user doesn't lose their input.
     */
    const save = async () => {
        if (savingRef.current) return;
        // A half-typed native date input reports value '' - saving it would silently erase the stored date
        if (dateRef.current && !dateRef.current.validity.valid) {
            setDateInvalid(true);
            dateRef.current.focus();
            return;
        }
        setDateInvalid(false);
        savingRef.current = true;
        setSaving(true);
        const ok = await onSave({ ...v, OtherId: v.AHP });
        savingRef.current = false;
        setSaving(false);
        if (ok) onClose();
    }

    return (
        <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth fullScreen={fullScreen}>
            <DialogTitle>{initial ? 'Edit Vehicle' : 'Add Vehicle'}</DialogTitle>
            <DialogContent dividers>
                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    {FIELD_DEFS.filter(f => initial || !f.editOnly).map(({ name, label, xs = 12, sm = 6, multiline, minRows, inputProps, type, InputLabelProps }) => (
                        <Grid item xs={xs} sm={sm} key={name}>
                            <TextField
                                name={name}
                                label={label}
                                type={type}
                                value={v[name] ?? ''}
                                onChange={set}
                                fullWidth
                                multiline={!!multiline}
                                minRows={multiline ? minRows : undefined}
                                inputProps={type === 'date' ? { ...inputProps, max: today } : inputProps}
                                inputRef={type === 'date' ? dateRef : undefined}
                                onBlur={type === 'date' ? (e) => setDateInvalid(!e.target.validity.valid) : undefined}
                                InputLabelProps={InputLabelProps}
                                error={type === 'date' && dateInvalid}
                                helperText={type === 'date' && dateInvalid ? 'Enter a complete date between 1980 and today, or clear the field' : undefined}
                            />
                        </Grid>
                    ))}
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>Cancel</Button>
                <Button variant="contained" onClick={save}>Save</Button>
            </DialogActions>
        </Dialog>
    );
}

export default VehicleDialog;
